import { useEffect, useRef, useState } from "react";
import { MAX_RECORDING_DURATION_MS, MIN_AUDIO_BYTES } from "@/constants/audio";
import { api } from "@/services/api";

function getSpeechRecognitionConstructor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition;
}

function canRecordAudio() {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  return Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
}

function cancelBrowserSpeech() {
  if (typeof window === "undefined") return;
  window.speechSynthesis?.cancel();
}

function extractSpeechTranscript(event) {
  let finalTranscript = "";
  let interimTranscript = "";
  for (let index = event.resultIndex; index < event.results.length; index += 1) {
    const transcript = event.results[index]?.[0]?.transcript || "";
    if (event.results[index].isFinal) finalTranscript += transcript;
    else interimTranscript += transcript;
  }
  return { finalTranscript, spokenText: (finalTranscript || interimTranscript).trim() };
}

function createSpeechRecognition({ onTranscript, setIsListening, setSpeechStatus }) {
  const SpeechRecognition = getSpeechRecognitionConstructor();
  if (!SpeechRecognition) return null;

  const recognition = new SpeechRecognition();
  recognition.lang = "en-US";
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
  recognition.onstart = () => {
    setIsListening(true);
    setSpeechStatus("Ouvindo... fale uma frase curta em inglês.");
  };
  recognition.onspeechstart = () => setSpeechStatus("Fala detectada. Continue até terminar a frase.");
  recognition.onspeechend = () => setSpeechStatus("Processando sua fala...");
  recognition.onend = () => {
    setIsListening(false);
    setSpeechStatus((currentStatus) => currentStatus || "Microfone pausado. Confira o texto antes de enviar.");
  };
  recognition.onerror = (event) => {
    setIsListening(false);
    const errorMessages = {
      "no-speech": "Não ouvi nenhuma fala. Clique novamente e fale perto do microfone.",
      "audio-capture": "Não consegui acessar o microfone. Confira se ele está conectado e liberado.",
      "not-allowed": "Permissão do microfone negada. Libere o microfone no cadeado do navegador.",
      network: "O reconhecimento de voz do navegador falhou por rede. Tente novamente ou digite a resposta.",
    };
    setSpeechStatus(errorMessages[event.error] || "Não consegui transcrever. Tente novamente ou digite sua resposta.");
  };
  recognition.onresult = (event) => {
    const { finalTranscript, spokenText } = extractSpeechTranscript(event);
    if (!spokenText) return;
    onTranscript(spokenText);
    setSpeechStatus(finalTranscript ? "Transcrição pronta. Revise e envie." : "Transcrevendo em tempo real...");
  };
  return recognition;
}

function getSupportedMimeType() {
  if (typeof window === "undefined") return "audio/webm";
  return window.MediaRecorder?.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
}

function stopStreamTracks(stream) {
  stream?.getTracks().forEach((track) => track.stop());
}

function clearRecordingTimer(recordingTimerRef) {
  if (!recordingTimerRef.current) return;
  if (typeof window === "undefined") return;
  window.clearTimeout(recordingTimerRef.current);
  recordingTimerRef.current = null;
}

function createAudioBlob(audioChunks) {
  return new Blob(audioChunks, { type: "audio/webm" });
}

async function getMicrophoneStream() {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
}

function getMicrophoneErrorMessage(error) {
  const errorName = error?.name || "";
  if (errorName === "NotAllowedError" || errorName === "SecurityError") {
    return "Permissão do microfone bloqueada. Clique no cadeado do navegador e permita o microfone.";
  }
  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return "Não encontrei microfone neste aparelho. Conecte um microfone ou digite sua resposta.";
  }
  if (errorName === "NotReadableError" || errorName === "TrackStartError") {
    return "O microfone está sendo usado por outro aplicativo. Feche chamadas/gravadores e tente novamente.";
  }
  return "Não consegui abrir o microfone. Vou tentar o reconhecimento de voz do navegador.";
}

async function testMicrophoneAccess() {
  const stream = await recorderHelpers.getMicrophoneStream();
  recorderHelpers.stopStreamTracks(stream);
}

function cleanupActiveRecorderResources(resources) {
  recorderHelpers.cleanupRecorderResources(resources);
}

function attachRecorderEvents({ audioChunksRef, recorder, setIsListening, setSpeechStatus, stopRecordingTracks, transcribeRecordedAudio }) {
  recorder.ondataavailable = (event) => {
    if (event.data?.size > 0) audioChunksRef.current.push(event.data);
  };
  recorder.onstart = () => {
    setIsListening(true);
    setSpeechStatus("Gravando... fale sua resposta em inglês e clique em parar.");
  };
  recorder.onerror = () => {
    setIsListening(false);
    stopRecordingTracks();
    setSpeechStatus("Erro ao gravar. Confira a permissão do microfone e tente novamente.");
  };
  recorder.onstop = () => {
    const audioBlob = createAudioBlob(audioChunksRef.current);
    setIsListening(false);
    stopRecordingTracks();
    transcribeRecordedAudio(audioBlob);
  };
}

function armRecordingTimeout({ mediaRecorderRef, recordingTimerRef }) {
  recordingTimerRef.current = window.setTimeout(() => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
  }, MAX_RECORDING_DURATION_MS);
}

function cleanupRecorderResources({ mediaRecorderRef, recognitionRef, recordingStreamRef, recordingTimerRef }) {
  recognitionRef.current?.abort?.();
  if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
  stopStreamTracks(recordingStreamRef.current);
  recordingStreamRef.current = null;
  clearRecordingTimer(recordingTimerRef);
}

const recorderHelpers = {
  armRecordingTimeout,
  attachRecorderEvents,
  canRecordAudio,
  cancelBrowserSpeech,
  cleanupRecorderResources,
  clearRecordingTimer,
  createSpeechRecognition,
  getMicrophoneStream,
  getSpeechRecognitionConstructor,
  getSupportedMimeType,
  stopStreamTracks,
  testMicrophoneAccess,
  cleanupActiveRecorderResources,
};

function useTranscription(onTranscript) {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechStatus, setSpeechStatus] = useState("");

  async function transcribeRecordedAudio(audioBlob) {
    if (!audioBlob || audioBlob.size < MIN_AUDIO_BYTES) {
      setSpeechStatus("Gravação muito curta. Clique de novo, fale uma frase e depois pare a gravação.");
      return;
    }
    setIsTranscribing(true);
    setSpeechStatus("Transcrevendo com Whisper... aguarde alguns segundos.");
    try {
      const result = await api.transcribeAudio(audioBlob);
      onTranscript(result.text || "");
      setSpeechStatus(result.text ? "Transcrição pronta. Revise e envie." : "Não consegui detectar fala. Tente novamente.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setSpeechStatus(detail || "Não consegui transcrever o áudio. Tente novamente ou digite a resposta.");
    } finally {
      setIsTranscribing(false);
    }
  }

  return { isTranscribing, setSpeechStatus, speechStatus, transcribeRecordedAudio };
}

function useSpeechRecognitionFallback({ onTranscript, setIsListening, setSpeechStatus }) {
  const recognitionRef = useRef(null);

  function startBrowserSpeechRecognition() {
    const recognition = recorderHelpers.createSpeechRecognition({ onTranscript, setIsListening, setSpeechStatus });
    if (!recognition) {
      setSpeechStatus("Reconhecimento de voz indisponível neste navegador. Digite sua resposta para continuar.");
      return;
    }
    recorderHelpers.cancelBrowserSpeech();
    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (error) {
      setIsListening(false);
      setSpeechStatus("O microfone já estava ativo. Aguarde um segundo e tente novamente.");
    }
  }

  function stopSpeechRecognition() {
    recognitionRef.current?.stop();
    setIsListening(false);
    setSpeechStatus("Microfone pausado. Confira o texto antes de enviar.");
  }

  function abortSpeechRecognition() {
    recognitionRef.current?.abort?.();
  }

  return { abortSpeechRecognition, recognitionRef, startBrowserSpeechRecognition, stopSpeechRecognition };
}

function useMediaRecorder({ setIsListening, setSpeechStatus, startBrowserSpeechRecognition, stopSpeechRecognition, transcribeRecordedAudio }) {
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);

  function stopRecordingTracks() {
    recorderHelpers.stopStreamTracks(recordingStreamRef.current);
    recordingStreamRef.current = null;
    recorderHelpers.clearRecordingTimer(recordingTimerRef);
  }

  async function startRecording() {
    if (!recorderHelpers.canRecordAudio()) {
      startBrowserSpeechRecognition();
      return;
    }
    try {
      recorderHelpers.cancelBrowserSpeech();
      audioChunksRef.current = [];
      const stream = await recorderHelpers.getMicrophoneStream();
      recordingStreamRef.current = stream;
      const mimeType = recorderHelpers.getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      recorderHelpers.attachRecorderEvents({ audioChunksRef, recorder, setIsListening, setSpeechStatus, stopRecordingTracks, transcribeRecordedAudio });
      recorder.start();
      recorderHelpers.armRecordingTimeout({ mediaRecorderRef, recordingTimerRef });
    } catch (error) {
      setIsListening(false);
      stopRecordingTracks();
      setSpeechStatus(getMicrophoneErrorMessage(error));
      startBrowserSpeechRecognition();
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current?.state === "recording") {
      setSpeechStatus("Finalizando gravação...");
      mediaRecorderRef.current.stop();
      return;
    }
    stopSpeechRecognition();
  }

  function cleanupRecording() {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    stopRecordingTracks();
  }

  return { cleanupRecording, mediaRecorderRef, recordingStreamRef, recordingTimerRef, startRecording, stopRecording };
}

export function useAudioRecording({ disabled = false, onTranscript }) {
  const recorderHelpersRef = useRef(recorderHelpers);
  const [isListening, setIsListening] = useState(false);
  const [isTestingMicrophone, setIsTestingMicrophone] = useState(false);
  const { isTranscribing, setSpeechStatus, speechStatus, transcribeRecordedAudio } = useTranscription(onTranscript);
  const speechRecognition = useSpeechRecognitionFallback({ onTranscript, setIsListening, setSpeechStatus });
  const mediaRecorder = useMediaRecorder({
    setIsListening,
    setSpeechStatus,
    startBrowserSpeechRecognition: speechRecognition.startBrowserSpeechRecognition,
    stopSpeechRecognition: speechRecognition.stopSpeechRecognition,
    transcribeRecordedAudio,
  });
  const { mediaRecorderRef, recordingStreamRef, recordingTimerRef } = mediaRecorder;
  const { recognitionRef } = speechRecognition;

  function handleMicrophoneClick() {
    if (disabled || isTranscribing) return;
    if (isListening) mediaRecorder.stopRecording();
    else mediaRecorder.startRecording();
  }

  async function handleMicrophoneTest() {
    if (disabled || isListening || isTranscribing || isTestingMicrophone) return;
    setIsTestingMicrophone(true);
    setSpeechStatus("Testando microfone... permita o acesso se o navegador pedir.");
    try {
      await recorderHelpers.testMicrophoneAccess();
      setSpeechStatus("Microfone pronto. Agora clique em Gravar voz e fale sua resposta.");
    } catch (error) {
      setSpeechStatus(getMicrophoneErrorMessage(error));
    } finally {
      setIsTestingMicrophone(false);
    }
  }

  useEffect(() => () => {
    recorderHelpersRef.current.cleanupActiveRecorderResources({
      mediaRecorderRef,
      recognitionRef,
      recordingStreamRef,
      recordingTimerRef,
    });
  }, [mediaRecorderRef, recognitionRef, recorderHelpersRef, recordingStreamRef, recordingTimerRef]);

  return {
    handleMicrophoneClick,
    handleMicrophoneTest,
    isListening,
    isTestingMicrophone,
    isTranscribing,
    speechStatus,
    speechRecognitionAvailable: Boolean(recorderHelpers.getSpeechRecognitionConstructor()),
    supportsAudioRecording: recorderHelpers.canRecordAudio(),
  };
}